import React from 'react';
import PropTypes from 'prop-types';
import StudentRepository from '../services/StudentRepository.js';
import {
  TextField,
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import FormButtonGroup from '../components/layout/FormButtonGroup.jsx';

export default function ProgramSynthesis({
  repo,
  handleScreenNext,
  handleScreenBack,
  handleErrors,
  formValues,
  setFormValues,
  onSkip,
}) {
  const [loading, setLoading] = React.useState(false);
  const handleValueChange = (evt) => {
    const name = evt.target.name;
    const value = evt.target.value;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  const handleCompetencyChange = (evt) => {
    const name = evt.target.name;
    let competencies = formValues.competencies.length ? formValues.competencies : [];
    if (evt.target.checked) {
      if (competencies.indexOf(name) === -1) competencies.push(name);
    } else {
      if (competencies.indexOf(name) >= 0) {
        competencies = competencies.filter(function (value) {
          return value !== name;
        });
      }
    }
    setFormValues({
      ...formValues,
      competencies: competencies,
    });
  };
  const isShiftable = () => {
    return formValues.competencies.length > 0 && !loading;
  };
  const handleNext = async () => {
    setLoading(true);
    repo
      .updateProgramSynthesis(formValues.competencies, formValues.suggestions)
      .then(() => {
        setLoading(false);
        handleScreenNext();
      })
      .catch(() => {
        setLoading(false);
        handleErrors(
          <Alert variant="filled" severity="error" sx={{ marginBottom: 3 }}>
            <Typography variant="caption">
              We are unable to submit your answers. Please contact the administrator at
              webteam@msuiit.edu.ph.
            </Typography>
          </Alert>
        );
      });
  };
  return (
    <React.Fragment>
      <FormControl component="fieldset" variant="standard">
        <FormLabel component="legend">
          What competencies in the program curriculum did you find significantly useful in your job?
          You may check more than one answer.
        </FormLabel>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length
                    ? formValues.competencies.includes('communication_skills')
                    : false
                }
                onChange={handleCompetencyChange}
                name="communication_skills"
              />
            }
            label="Communication skills"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length
                    ? formValues.competencies.includes('hr_skills')
                    : false
                }
                onChange={handleCompetencyChange}
                name="hr_skills"
              />
            }
            label="Human Relations skills"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length
                    ? formValues.competencies.includes('entrepreneurial_skills')
                    : false
                }
                onChange={handleCompetencyChange}
                name="entrepreneurial_skills"
              />
            }
            label="Entrepreneurial skills"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length
                    ? formValues.competencies.includes('problem_solving_skills')
                    : false
                }
                onChange={handleCompetencyChange}
                name="problem_solving_skills"
              />
            }
            label="Problem-solving skills"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length
                    ? formValues.competencies.includes('critical_thinking_skills')
                    : false
                }
                onChange={handleCompetencyChange}
                name="critical_thinking_skills"
              />
            }
            label="Critical Thinking skills"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  formValues.competencies.length > 0 &&
                  formValues.competencies.filter(
                    (x) =>
                      ![
                        'communication_skills',
                        'hr_skills',
                        'entrepreneurial_skills',
                        'problem_solving_skills',
                        'critical_thinking_skills',
                      ].includes(x)
                  ).length > 0
                }
                onChange={handleCompetencyChange}
                name="other"
              />
            }
            label="Other"
          />
        </FormGroup>
      </FormControl>
      <FormGroup>
        <FormLabel component="legend">
          Please list down any suggestion to further improve the program curriculum.
        </FormLabel>
        <TextField
          required
          id="suggestions"
          name="suggestions"
          variant="outlined"
          onChange={handleValueChange}
          value={formValues.suggestions ? formValues.suggestions : ''}
          fullWidth
        />
      </FormGroup>
      <Box sx={{ marginTop: '20px' }}>
        <FormButtonGroup>
          <Button variant="contained" size="large" color="error" onClick={() => handleScreenBack()}>
            Back
          </Button>
          <Button
            variant="outlined"
            size="large"
            color="primary"
            onClick={onSkip}
            sx={{ marginRight: '10px' }}
          >
            Skip to Finish
          </Button>
          <Box sx={{ m: 0, position: 'relative' }}>
            <Button
              variant="contained"
              size="large"
              color="primary"
              disabled={!isShiftable()}
              onClick={handleNext}
            >
              Next
            </Button>
            {loading && (
              <CircularProgress
                size={24}
                color={'success'}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        </FormButtonGroup>
      </Box>
    </React.Fragment>
  );
}

ProgramSynthesis.propTypes = {
  repo: PropTypes.instanceOf(StudentRepository),
  studentId: PropTypes.string,
  handleScreenNext: PropTypes.func,
  handleScreenBack: PropTypes.func,
  handleErrors: PropTypes.func,
  formValues: PropTypes.object,
  setFormValues: PropTypes.func,
  onSkip: PropTypes.func,
};
